import type { KarlBoardDecision, KarlQuestion, KarlSourceOrigin } from "../../karlSteps";
import type {
  BuildSourceRoster,
  KarlSourceEntry,
  KarlSourceStatus,
} from "./types";
import { isSameBoard, normalizeBoardName } from "../../boardNames";

interface StatusArgs {
  origin: KarlSourceOrigin | null;
  decidedBy: string;
  isExcluded: boolean;
  /** True when the plan draws this board, whether or not cable starts there. */
  isDrawn: boolean;
  circuitCount: number;
}

const statusOf = ({
  origin,
  decidedBy,
  isExcluded,
  isDrawn,
  circuitCount,
}: StatusArgs): KarlSourceStatus => {
  if (origin === "added") return "added";
  if (!isDrawn) return "absent";
  if (isExcluded) return "excluded";
  if (decidedBy === "question") return "open";
  if (!origin) return "idle";
  if (origin === "estimated") return "estimated";
  return circuitCount > 0 ? "feeding" : "idle";
};

interface SettledArgs extends Pick<KarlSourceEntry, "status" | "origin"> {
  isStruck: boolean;
  isMoved: boolean;
  isConfirmed: boolean;
}

const isSettledOf = ({
  status,
  origin,
  isStruck,
  isMoved,
  isConfirmed,
}: SettledArgs): boolean => {
  // The estimator's own hand outranks anything the run decided.
  if (isStruck || isMoved || isConfirmed) return true;
  if (status === "open" || status === "absent" || status === "estimated") {
    return false;
  }
  return origin !== "unknown";
};

const RANK: Record<KarlSourceStatus, number> = {
  open: 1,
  estimated: 2,
  feeding: 3,
  idle: 4,
  excluded: 5,
  added: 6,
  absent: 7,
};

// The main board is what every other answer hangs off, so it is judged first
// even though the run is most confident about it.
const rankOf = (entry: KarlSourceEntry): number =>
  entry.isMain ? 0 : RANK[entry.status];

const questionFor = (board: string, questions: KarlQuestion[]): string | null =>
  questions.find(
    (question) =>
      question.step === "sources" &&
      question.subjects.some((subject) => normalizeBoardName(subject) === normalizeBoardName(board)),
  )?.id ?? null;

export const buildSourceRoster: BuildSourceRoster = ({
  sources,
  schematicBoards,
  agent,
  added,
  removed,
  placed,
  confirmed,
  mainBoardName,
  planBoards = [],
}) => {
  const isMainBoard = (fallback: boolean, name: string, label: string): boolean =>
    mainBoardName ? isSameBoard({ board: mainBoardName, name, label }) : fallback;
  // A board the run never fed from still has a marker on the plan, and every board answer is keyed by it.
  const planBoardOf = (board: string) =>
    planBoards.find((planBoard) => isSameBoard({ board, name: planBoard.name, label: planBoard.label }));
  const confirmedKeys = new Set(confirmed);
  const claimed = new Set<string>();

  const entryOf = (
    partial: Omit<KarlSourceEntry, "status" | "isSettled" | "isStruck" | "isMoved">,
    decision: KarlBoardDecision | null,
    isDrawn: boolean,
  ): KarlSourceEntry => {
    // A board with no marker of its own is struck under its roster key.
    const isStruck = Boolean(removed[partial.label || partial.key]);
    const isMoved = Boolean(partial.label && placed[partial.label]);
    const status = statusOf({
      origin: partial.origin,
      decidedBy: partial.decidedBy,
      isExcluded: Boolean(decision?.isExcluded),
      isDrawn,
      circuitCount: partial.circuitCount,
    });
    return {
      ...partial,
      status,
      isStruck,
      isMoved,
      isSettled: isSettledOf({
        status,
        origin: partial.origin,
        isStruck,
        isMoved,
        isConfirmed: confirmedKeys.has(partial.key),
      }),
    };
  };

  const fromRun = sources.map((source) => {
    const decision =
      agent.decisions.find((entry) =>
        isSameBoard({ board: entry.board, name: source.name, label: source.label }),
      ) ?? null;
    if (decision) claimed.add(decision.board);
    const board = decision?.board ?? source.name;
    return entryOf(
      {
        key: source.label || source.name,
        label: source.label,
        name: source.name,
        board,
        origin: source.origin,
        placedBy: source.placedBy,
        decidedBy: decision?.decidedBy ?? "",
        system: decision?.system ?? "",
        isMain: isMainBoard(
          decision?.isMain ?? agent.mainBoard === board,
          source.name,
          source.label,
        ),
        position: source.position,
        circuitCount: source.circuitCount,
        deviceCount: source.deviceCount,
        lengthM: source.lengthM,
        questionId: questionFor(board, agent.questions),
      },
      decision,
      true,
    );
  });

  const byHand = Object.entries(added).map(([label, source]) =>
    entryOf(
      {
        key: label,
        label,
        name: source.name,
        board: source.name,
        origin: "added",
        placedBy: "",
        decidedBy: "",
        system: "",
        isMain: false,
        position: { x: source.x, y: source.y },
        circuitCount: 0,
        deviceCount: 0,
        lengthM: 0,
        questionId: null,
      },
      null,
      true,
    ),
  );

  // A board the plan draws that never became a feed point is still the
  // estimator's business: it is where the run's open questions live.
  const drawnOnly = agent.decisions
    .filter((decision) => !claimed.has(decision.board))
    .map((decision) => {
      const planBoard = planBoardOf(decision.board);
      return entryOf(
        {
          key: decision.board,
          label: planBoard?.label ?? "",
          name: decision.board,
          board: decision.board,
          origin: null,
          placedBy: "",
          decidedBy: decision.decidedBy,
          system: decision.system,
          isMain: isMainBoard(decision.isMain, decision.board, planBoard?.label ?? ""),
          position: planBoard?.position ?? null,
          circuitCount: 0,
          deviceCount: 0,
          lengthM: 0,
          questionId: questionFor(decision.board, agent.questions),
        },
        decision,
        true,
      );
    });

  const placedNames = [...fromRun, ...byHand, ...drawnOnly];
  const namedOnly = schematicBoards
    .filter(
      (board) =>
        !placedNames.some((entry) => isSameBoard({ board, name: entry.name, label: entry.label })),
    )
    .map((board) => {
      const planBoard = planBoardOf(board);
      return entryOf(
        {
          key: board,
          label: planBoard?.label ?? "",
          name: board,
          board,
          origin: null,
          placedBy: "",
          decidedBy: "",
          system: "",
          isMain: isMainBoard(agent.mainBoard === board, board, planBoard?.label ?? ""),
          position: planBoard?.position ?? null,
          circuitCount: 0,
          deviceCount: 0,
          lengthM: 0,
          questionId: null,
        },
        null,
        Boolean(planBoard),
      );
    });

  const listed = [...fromRun, ...byHand, ...drawnOnly, ...namedOnly];
  const onPlanOnly = planBoards
    .filter(
      (planBoard) =>
        !listed.some(
          (entry) =>
            entry.label === planBoard.label ||
            isSameBoard({ board: entry.board, name: planBoard.name, label: planBoard.label }),
        ),
    )
    .map((planBoard) =>
      entryOf(
        {
          key: planBoard.label,
          label: planBoard.label,
          name: planBoard.name,
          board: planBoard.name,
          origin: null,
          placedBy: "",
          decidedBy: "",
          system: "",
          isMain: isMainBoard(agent.mainBoard === planBoard.name, planBoard.name, planBoard.label),
          position: planBoard.position,
          circuitCount: 0,
          deviceCount: 0,
          lengthM: 0,
          questionId: questionFor(planBoard.name, agent.questions),
        },
        null,
        true,
      ),
    );

  return [...listed, ...onPlanOnly].sort((a, b) => {
    const byRank = rankOf(a) - rankOf(b);
    if (byRank !== 0) return byRank;
    const byLoad = b.circuitCount - a.circuitCount;
    if (byLoad !== 0) return byLoad;
    return a.name.localeCompare(b.name);
  });
};
